/**
 * The company's own PowerOffice Go (backend/services/poweroffice.js, issue #110, beta), the shape
 * of Tripletex.tsx for one store: the administrator connects it in Innstillinger, Lønn, and Timer til
 * lønn sends the month as salary lines. Connecting is Go's own approval page when the server offers it
 * (owner 2026-10-19: «Koble til PowerOffice Go», named so by Visma's terms 2.2.1, sends the company's org number, Go comes back here with
 * ?state=…&token=…), else the client key Go makes, pasted. The key is never read back.
 */
import { del, errorText, get, post, put } from '@/api/client';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Button, Chip, Confirm, Panel, toast } from '@/ui';
import { fmtD } from '@/ui/format';
import { Notice } from '@/ui/page';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { HeldScope, type HeldSendScope } from './HeldScope';
import { IntegrationCapability } from './IntegrationCapability';
import { invalidatePayMonth, when } from './Month';
import { PowerOfficeAccounts } from './PowerOfficeAccounts';
import { PowerOfficeContracts } from './PowerOfficeContracts';
import { PowerOfficeLeaves } from './PowerOfficeLeaves';
import { PowerOfficeMatchPanel } from './PowerOfficeMatch';
import { POWEROFFICE, type PayrollSystem, useLandOn } from './payrollSystem';

/** GET /admin/poweroffice */
export interface PowerOfficeStatus {
  available: boolean;
  /** Koble til through Go's approval page (POWEROFFICE_REDIRECT_URI set on the server) */
  onboarding?: boolean;
  connected: boolean;
  client_name: string | null;
  connected_at: string | null;
  /** sending to Go starts off until an administrator switches it on (#296 ONB-18) */
  outbound_on?: boolean;
  outbound_at?: string | null;
  /** 'unknown': Go did not answer a line; 'partial': lines were left in Go (#296 ONB-19) */
  sent: {
    year: number;
    month: number;
    lines: number;
    sent_at: string;
    outcome?: 'sent' | 'unknown' | 'partial';
    reference?: string | null;
    /** an unknown or partial send: what it contained against the month's lines now (PROM-23) */
    scope?: HeldSendScope;
  }[];
  /** the store's department in the Go company (#183) */
  department?: { id: number; code: string; name: string } | null;
  /** or its location, for a chain that tells stores apart by location (#183 phase 3) */
  location?: { id: number; name: string } | null;
}
/** GET /admin/poweroffice/plan */
export interface PowerOfficePlan {
  year: number;
  month: number;
  client_name: string | null;
  people: number;
  lines: { user_id: number; name: string; pay_type: string; count: number; rate: number | null }[];
  missing_people: {
    user_id: number;
    name: string;
    employee_number: string | null;
    /** shared_number: another person in the month has the same number (#168) */
    reason: 'no_number' | 'not_in_poweroffice' | 'shared_number';
  }[];
  missing_types: {
    pay_type: string;
    code: string | null;
    reason: 'no_code' | 'not_in_poweroffice' | 'fixed_without_rate';
  }[];
  already_sent: {
    sent_at: string;
    outcome?: 'sent' | 'unknown' | 'partial';
    reference?: string | null;
  } | null;
  /** false while sending is off for the store (#296 ONB-18) */
  outbound_on?: boolean;
  /** project numbers on the month's lines that Go has no project for (#183) */
  missing_projects?: { number: string; reason: 'not_in_poweroffice' }[];
  /** the hours this plan was made from (#265) */
  revision?: string;
  /** the month already left as a PowerOffice file (#183) */
  file_exported?: { exported_at: string; lines: number } | null;
}

export const KEY = (t?: number) => ['poweroffice', t];
export function usePowerOffice(enabled = true) {
  const { tenant } = useTenant();
  return useQuery({
    queryKey: KEY(tenant?.id),
    queryFn: () => get<PowerOfficeStatus>('/admin/poweroffice'),
    enabled,
  });
}
const mm = (y: number, m: number) => `${String(m).padStart(2, '0')}.${y}`;

/**
 * Endringer i PowerOffice or Tripletex (#183 phase 2, #187): new hires there and people who have ended there while still
 * members here. New hires are added from Koble ansatte; a leaver's end date is set for the ticked ones.
 */
export function StaffChanges({ onAdd, sys = POWEROFFICE }: { onAdd: () => void; sys?: PayrollSystem }) {
  const qc = useQueryClient();
  type Changes = {
    new_hires: { number: string; name: string; birth_date?: string | null }[];
    leavers: { user_id: number; name: string; number: string; end_date: string | null; archived: boolean }[];
  } | null;
  const q = useQuery({
    queryKey: [sys.key, 'changes'],
    queryFn: () => get<{ changes: Changes }>(`${sys.base}/changes`),
  });
  const [pick, setPick] = useState<Set<number>>(new Set());
  const end = useMutation({
    mutationFn: () => put<{ changes: Changes }>(`${sys.base}/changes`, { leavers: [...pick] }),
    onSuccess: (r) => {
      qc.setQueryData([sys.key, 'changes'], r);
      qc.invalidateQueries({ queryKey: ['users'] });
      setPick(new Set());
      toast(T('Sluttdato satt.'));
    },
    onError: (e) => toast(errorText(e)),
  });
  const c = q.data?.changes;
  if (!c || (!c.new_hires.length && !c.leavers.length)) return null;
  return (
    <Notice tone="warn" title={T('Endringer i {system}', { system: sys.short })}>
      <span data-testid="po-changes" style={{ display: 'grid', gap: 6 }}>
        {c.new_hires.length > 0 && (
          <span>
            {T('{n} nye ansatte i {system}: {names}.', {
              n: c.new_hires.length,
              system: sys.short,
              names: c.new_hires
                .map((h) => (h.birth_date === null ? `${h.name} (${T('mangler fødselsdato')})` : h.name))
                .join(', '),
            })}{' '}
            {c.new_hires.some((h) => h.birth_date !== null) ? (
              <button type="button" className="lnk" onClick={onAdd}>
                {T('Legg dem til')}
              </button>
            ) : (
              T('Legg inn fødselsdato i {system} først, så kan de legges til.', { system: sys.short })
            )}
          </span>
        )}
        {c.leavers.length > 0 && (
          <>
            <span>{T('Har sluttet i {system}, men er fortsatt ansatt her:', { system: sys.short })}</span>
            {c.leavers.map((l) => (
              <label key={l.user_id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={pick.has(l.user_id)}
                  onChange={(e) => {
                    const next = new Set(pick);
                    if (e.target.checked) next.add(l.user_id);
                    else next.delete(l.user_id);
                    setPick(next);
                  }}
                />
                {l.name} ({l.number}),{' '}
                {l.end_date ? T('sluttet {d}', { d: fmtD(l.end_date) }) : T('arkivert')}
              </label>
            ))}
            <span>
              <Button small onClick={() => end.mutate()} disabled={end.isPending || pick.size === 0}>
                {T('Sett sluttdato for {n}', { n: pick.size })}
              </Button>
            </span>
          </>
        )}
      </span>
    </Notice>
  );
}

/**
 * This store's department in the PowerOffice company (#183 phase 2): its salary lines carry it, and
 * its file's «avdeling» field. A department named like the store is suggested, never saved unasked.
 */
/** The store's location in Go, for a chain that tells its stores apart by location (#183 phase 3). Hidden when Go has none. */
function StoreLocation({
  status,
  onSaved,
}: { status: PowerOfficeStatus; onSaved: (s: PowerOfficeStatus) => void }) {
  const q = useQuery({
    queryKey: ['poweroffice-locations'],
    queryFn: () =>
      get<{ current: number | null; locations: { id: number; code: string; name: string }[] }>(
        '/admin/poweroffice/locations',
      ),
  });
  const save = useMutation({
    mutationFn: (location_id: number | null) =>
      put<PowerOfficeStatus>('/admin/poweroffice/location', { location_id }),
    onSuccess: (s) => {
      onSaved(s);
      toast(s.location ? T('Lokasjon lagret: {name}', { name: s.location.name }) : T('Lokasjon fjernet'));
    },
    onError: (e) => toast(errorText(e)),
  });
  const list = q.data?.locations ?? [];
  if (!list.length) return null;
  return (
    <label style={{ display: 'grid', gap: 4, fontSize: 14 }} data-testid="po-location">
      {T('Lokasjon i PowerOffice for denne butikken')}
      <select
        className="inp"
        value={status.location?.id ?? ''}
        disabled={save.isPending}
        onChange={(e) => save.mutate(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">{T('Ingen lokasjon')}</option>
        {list.map((l) => (
          <option key={l.id} value={l.id}>
            {l.code}, {l.name}
          </option>
        ))}
      </select>
      <small style={{ color: 'var(--ink3)' }}>
        {T('Bare nødvendig hvis butikkene skilles på lokasjon i PowerOffice i stedet for avdeling.')}
      </small>
    </label>
  );
}

/** The store's department in its payroll system (PowerOffice Go #183, Tripletex #187). */
export function StoreDepartment<S extends { department?: { id: number; name: string } | null }>({
  status,
  onSaved,
  sys = POWEROFFICE,
}: { status: S; onSaved: (s: S) => void; sys?: PayrollSystem }) {
  const q = useQuery({
    queryKey: [sys.key, 'departments'],
    queryFn: () =>
      get<{
        current: number | null;
        suggestion: number | null;
        departments: { id: number; code: string; name: string }[];
      }>(`${sys.base}/departments`),
  });
  const save = useMutation({
    mutationFn: (department_id: number | null) => put<S>(`${sys.base}/department`, { department_id }),
    onSuccess: (s) => {
      onSaved(s);
      toast(s.department ? T('Avdeling lagret: {name}', { name: s.department.name }) : T('Avdeling fjernet'));
    },
    onError: (e) => toast(errorText(e)),
  });
  const list = q.data?.departments ?? [];
  if (!list.length) return null;
  const value = status.department?.id ?? '';
  const hint =
    !status.department && q.data?.suggestion ? list.find((d) => d.id === q.data?.suggestion) : null;
  return (
    <label style={{ display: 'grid', gap: 4, fontSize: 14 }} data-testid="po-department">
      {T('Avdeling i {system} for denne butikken', { system: sys.short })}
      <select
        className="inp"
        value={value}
        disabled={save.isPending}
        onChange={(e) => save.mutate(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">{T('Ingen avdeling')}</option>
        {list.map((d) => (
          <option key={d.id} value={d.id}>
            {d.code ? `${d.code}, ${d.name}` : d.name}
          </option>
        ))}
      </select>
      {hint && (
        <small style={{ color: 'var(--ink3)' }}>
          {T('Forslag: {name}', { name: hint.code ? `${hint.code}, ${hint.name}` : hint.name })}{' '}
          <button type="button" className="lnk" onClick={() => save.mutate(hint.id)}>
            {T('Bruk')}
          </button>
        </small>
      )}
    </label>
  );
}

/** Innstillinger, Lønn: connect with the client key from PowerOffice Go, or disconnect. */
export function PowerOfficeConnect() {
  const q = usePowerOffice();
  const qc = useQueryClient();
  const { tenant } = useTenant();
  const nav = useNavigate();
  const [key, setKey] = useState('');
  // Koble ansatte og lønnsarter opens by itself once connected: matching is the next step
  const [matching, setMatching] = useState(false);
  // pay and contract against PowerOffice (#183 phase 2)
  const [comparing, setComparing] = useState(false);
  // the store's revenue and labour accounts in Go (#183 phase 3)
  const [accounts, setAccounts] = useState(false);
  const [leaves, setLeaves] = useState(false);
  const done = (s: PowerOfficeStatus) => qc.setQueryData(KEY(tenant?.id), s);
  const connect = useMutation({
    mutationFn: () => put<PowerOfficeStatus>('/admin/poweroffice', { key }),
    onSuccess: (s) => {
      done(s);
      setKey('');
      setMatching(true);
      toast(`${T('Koblet til PowerOffice')}${s.client_name ? `: ${s.client_name}` : ''}`);
    },
    onError: (e) => toast(errorText(e)),
  });
  // Go's approval page: the server sends the org number, the browser goes to Go and comes back here
  const start = useMutation({
    mutationFn: () => post<{ url: string }>('/admin/poweroffice/onboarding', {}),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast(errorText(e)),
  });
  const finish = useMutation({
    mutationFn: (b: { state: string; token: string | null; status: string | null }) =>
      post<PowerOfficeStatus>('/admin/poweroffice/onboarding/finish', b),
    onSuccess: (s) => {
      done(s);
      toast(`${T('Koblet til PowerOffice')}${s.client_name ? `: ${s.client_name}` : ''}`);
      // a company still in its first-run setup goes on there (Oppsett, 2026-10-19)
      if (tenant?.setup_step && tenant.me?.is_admin) nav('/oppsett');
      else setMatching(true);
    },
    onError: (e) => toast(errorText(e)),
  });
  // back from Go: finished once, and the token leaves the address bar
  const [params, setParams] = useSearchParams();
  const back = useRef(false);
  useEffect(() => {
    const state = params.get('state');
    if (!state || back.current) return;
    back.current = true;
    // [U] the parameter names Go uses; asked of PowerOffice
    const pick = (...k: string[]) => k.map((n) => params.get(n)).find((v) => v) ?? null;
    finish.mutate({
      state,
      token: pick('token', 'onboardingToken', 'OnboardingToken'),
      status: pick('status', 'Status'),
    });
    setParams({}, { replace: true });
  }, [params, setParams, finish]);
  const [asking, setAsking] = useState(false);
  const disconnect = useMutation({
    mutationFn: () => del<PowerOfficeStatus & { offboarded?: boolean | null }>('/admin/poweroffice'),
    onSuccess: (s) => {
      done(s);
      // PowerOffice may not let Timefly remove itself (a right not granted yet, #183): then the person does
      toast(
        s.offboarded === false
          ? T(
              'Koblet fra i Timefly. Fjern også Timefly under Meny, Innstillinger, Utvidelser i PowerOffice Go.',
            )
          : T('Koblet fra PowerOffice'),
      );
    },
    onError: (e) => toast(errorText(e)),
  });
  const s = q.data;
  useLandOn('poweroffice', !!s);
  if (!s) return null;
  return (
    <Panel
      id="poweroffice"
      title={
        <>
          {T('PowerOffice Go')}{' '}
          <Chip tone="warn" small>
            {T('Beta')}
          </Chip>
        </>
      }
      sub={T('Send godkjente timer rett til lønn i PowerOffice Go, i stedet for å laste opp filen.')}
    >
      {!s.available ? (
        <p style={{ margin: 0, color: 'var(--ink3)' }}>
          {T('Den direkte koblingen er ikke slått på ennå. Bruk PowerOffice-filen under Rapporter så lenge.')}
        </p>
      ) : s.connected ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <span>
            {T('Koblet til')} <b>{s.client_name ?? T('PowerOffice Go')}</b>
          </span>
          <StoreDepartment status={s} onSaved={done} />
          <StoreLocation status={s} onSaved={done} />
          <StaffChanges onAdd={() => setMatching(true)} />
          <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!matching && (
              <Button tone="pri" onClick={() => setMatching(true)}>
                {T('Koble ansatte og lønnsarter')}
              </Button>
            )}
            {!comparing && (
              <Button onClick={() => setComparing(true)}>{T('Sammenlign lønn og kontrakt')}</Button>
            )}
            {!accounts && (
              <Button onClick={() => setAccounts(true)}>{T('Kontoer for omsetning og lønn')}</Button>
            )}
            {!leaves && <Button onClick={() => setLeaves(true)}>{T('Permisjoner i PowerOffice')}</Button>}
            <Button onClick={() => setAsking(true)} disabled={disconnect.isPending}>
              {T('Koble fra')}
            </Button>
          </span>
          {/* Go revokes the client key on disconnect and shows a key only once (beta test 2026-11-05) */}
          <Confirm
            open={asking}
            title={T('Koble fra PowerOffice Go?')}
            yes={T('Koble fra')}
            busy={disconnect.isPending}
            onYes={() => disconnect.mutate(undefined, { onSettled: () => setAsking(false) })}
            onClose={() => setAsking(false)}
            testid="poweroffice-disconnect"
          >
            {T(
              'Klientnøkkelen blir også slettet i PowerOffice Go. For å koble til igjen må du lage en ny klientnøkkel i Go.',
            )}
          </Confirm>
          {matching && <PowerOfficeMatchPanel onClose={() => setMatching(false)} />}
          {comparing && <PowerOfficeContracts onClose={() => setComparing(false)} />}
          {accounts && <PowerOfficeAccounts onClose={() => setAccounts(false)} />}
          {leaves && <PowerOfficeLeaves onClose={() => setLeaves(false)} />}
          {/* directions, ownership, the sending switch and the last sends (#296 ONB-18, ONB-19) */}
          <IntegrationCapability
            sys={POWEROFFICE}
            name="PowerOffice Go"
            status={s}
            queryKey={KEY(tenant?.id)}
          />
        </div>
      ) : s.onboarding ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <p style={{ margin: 0, color: 'var(--ink2)', fontSize: 14 }}>
            {T(
              'Du logger inn i PowerOffice Go og godkjenner Timefly for bedriftens klient. Etterpå kobler du ansatte og lønnsarter.',
            )}
          </p>
          <span>
            <Button
              tone="pri"
              data-testid="po-onboard"
              onClick={() => start.mutate()}
              disabled={start.isPending || finish.isPending}
            >
              {finish.isPending ? T('Kobler til…') : T('Koble til PowerOffice Go')}
            </Button>
          </span>
        </div>
      ) : (
        <form
          style={{ display: 'grid', gap: 10 }}
          onSubmit={(e) => {
            e.preventDefault();
            connect.mutate();
          }}
        >
          <ol style={{ margin: 0, paddingLeft: 18, color: 'var(--ink2)', fontSize: 14 }}>
            <li>
              {T(
                'I PowerOffice Go: åpne Meny, Innstillinger, Utvidelser. Velg Egendefinert og lim inn applikasjonsnøkkelen du har fått fra Timefly.',
              )}
            </li>
            <li>{T('Kopier klientnøkkelen Go viser før du trykker OK. Go viser den bare én gang.')}</li>
            <li>{T('Lim den inn her og trykk Koble til.')}</li>
          </ol>
          <input
            className="inp"
            type="password"
            autoComplete="off"
            aria-label={T('Klientnøkkel fra PowerOffice')}
            placeholder={T('Klientnøkkel fra PowerOffice')}
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
          <span>
            <Button tone="pri" type="submit" disabled={connect.isPending || key.trim().length < 20}>
              {connect.isPending ? T('Kobler til…') : T('Koble til')}
            </Button>
          </span>
        </form>
      )}
    </Panel>
  );
}

/** Timer til lønn: see what the month becomes in PowerOffice Go, then send it once. One store at a time. */
export function PowerOfficeSend({
  from,
  to,
  allowed,
  scopeNote,
}: { from: string; to: string; allowed: boolean; scopeNote?: string | null }) {
  const q = usePowerOffice(allowed);
  const qc = useQueryClient();
  const { tenant } = useTenant();
  const [plan, setPlan] = useState<PowerOfficePlan | null>(null);
  const [busy, setBusy] = useState(false);
  const s = q.data;
  // what became of the sent month in Go (#183): asked once the plan says it was sent
  const sentQ = useQuery({
    queryKey: ['poweroffice-sent', tenant?.id, from],
    queryFn: () =>
      get<{
        status: {
          lines: number;
          locked: number;
          deleted: number;
          waiting: number;
          resendable: boolean;
        } | null;
      }>(`/admin/poweroffice/sent?start_date=${from}&end_date=${to}`),
    enabled: !!plan?.already_sent,
  });
  if (!allowed || !s?.available) return null;
  const [y, m] = from.split('-').map(Number);
  const sent = s.sent.find((x) => x.year === y && x.month === m);
  const look = async () => {
    setBusy(true);
    try {
      setPlan(await get<PowerOfficePlan>(`/admin/poweroffice/plan?start_date=${from}&end_date=${to}`));
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const send = async () => {
    setBusy(true);
    try {
      const r = await post<{ lines: number; people: number }>('/admin/poweroffice/send', {
        start_date: from,
        end_date: to,
        // the plan said the month already left as a file, and the button said «Send likevel» (#183)
        ...(plan?.file_exported ? { after_file: true } : {}),
        // the hours this plan was made from: changed since, the send is refused (#265)
        revision: plan?.revision,
      });
      toast(
        `${T('Sendt til PowerOffice')}: ${r.lines} ${T('lønnslinjer')}. ${T('Åpne Lønn i PowerOffice Go for å se over og kjøre lønnen.')}`,
      );
      setPlan(null);
      qc.invalidateQueries({ queryKey: KEY(tenant?.id) });
      invalidatePayMonth(qc);
    } catch (e) {
      toast(errorText(e));
      await look();
    } finally {
      setBusy(false);
    }
  };
  const st = sentQ.data?.status ?? null;
  // notArrived: the manager looked in Go after a send that stopped without a full answer (#296 ONB-19)
  const again = async (notArrived = false) => {
    setBusy(true);
    try {
      await post('/admin/poweroffice/release', {
        start_date: from,
        end_date: to,
        ...(notArrived ? { not_arrived: true } : {}),
      });
      if (notArrived) {
        toast(T('Måneden kan sendes på nytt.'));
        qc.invalidateQueries({ queryKey: KEY(tenant?.id) });
      }
      if (plan?.already_sent) await sentQ.refetch();
      await look();
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const blocked =
    !!plan &&
    (!!plan.already_sent ||
      plan.outbound_on === false ||
      plan.missing_people.length > 0 ||
      plan.missing_types.length > 0 ||
      (plan.missing_projects?.length ?? 0) > 0 ||
      plan.lines.length === 0);
  return (
    <div className="lexp" data-testid="export-poweroffice-api">
      <div className="lexpt">
        <b>
          {T('Send til PowerOffice')}{' '}
          <Chip tone="warn" small>
            {T('Beta')}
          </Chip>
        </b>
        <span>
          {s.connected
            ? T(
                'Lager lønnslinjer i PowerOffice Go med de samme tallene som filen. Du kjører lønnen i PowerOffice som før.',
              )
            : T('Ikke koblet til ennå.')}{' '}
          {!s.connected && <Link to="/innstillinger/lonn">{T('Koble til under Innstillinger, Lønn')}</Link>}
        </span>
        {sent && (sent.outcome ?? 'sent') === 'sent' && (
          <span data-testid="poweroffice-receipt">
            {T('Sendt {when}, {n} lønnslinjer.', { when: when(sent.sent_at), n: sent.lines })}
          </span>
        )}
        {/* a send that stopped without a full answer holds the month until somebody has looked in Go (#296 ONB-19) */}
        {sent?.outcome && sent.outcome !== 'sent' && (
          <span data-testid="poweroffice-unknown">
            {sent.outcome === 'partial'
              ? T(
                  'Sendingen {when} stoppet, og {n} lønnslinjer ble liggende i PowerOffice Go. Slett dem der før du gjør noe mer.',
                  { when: when(sent.sent_at), n: sent.lines },
                )
              : T(
                  'Sendt {when}, men PowerOffice svarte ikke. Se etter referansen {ref} under lønnslinjene i PowerOffice Go og slett den før du gjør noe mer.',
                  { when: when(sent.sent_at), ref: sent.reference ?? '' },
                )}{' '}
            <button type="button" className="lnk" disabled={busy} onClick={() => again(true)}>
              {T('Kom ikke fram, send på nytt')}
            </button>
          </span>
        )}
        {sent?.outcome && sent.outcome !== 'sent' && <HeldScope scope={sent.scope} />}
        {s.connected && scopeNote && <span className="lnote">{scopeNote}</span>}
      </div>
      {s.connected && !scopeNote && (
        <span className="lexpa">
          <Button onClick={look} disabled={busy}>
            {busy && !plan ? T('Henter…') : T('Se hva som sendes')}
          </Button>
        </span>
      )}
      {plan && !scopeNote && (
        <div className="lplan" data-testid="poweroffice-plan">
          <span>
            {mm(plan.year, plan.month)}: {plan.people} {plan.people === 1 ? T('ansatt') : T('ansatte')},{' '}
            {plan.lines.length} {plan.lines.length === 1 ? T('linje') : T('linjer')}
            {plan.client_name ? ` ${T('til')} ${plan.client_name}` : ''}.
          </span>
          {!!plan.missing_projects?.length && (
            <Notice tone="dgr" title={T('Prosjekter som ikke finnes i PowerOffice Go')}>
              <span data-testid="poweroffice-missing-projects">
                {plan.missing_projects.map((x) => x.number).join(', ')}.{' '}
                {T('Opprett prosjektet i PowerOffice med samme kode, eller rett nummeret under Prosjekter.')}
              </span>
            </Notice>
          )}
          {!plan.already_sent && plan.outbound_on === false && (
            <Notice tone="warn" title={T('Sending til PowerOffice Go er slått av.')}>
              <span data-testid="poweroffice-outbound-off">
                {T('En administrator slår den på under Innstillinger, Lønn. Filen kan fortsatt lastes ned.')}
              </span>
            </Notice>
          )}
          {plan.file_exported && !plan.already_sent && (
            <Notice tone="warn" title={T('Denne måneden er allerede lastet ned som PowerOffice-fil.')}>
              <span data-testid="poweroffice-file-exported">
                {T(
                  'Filen ble laget {when}. Er den importert i PowerOffice Go, blir timene betalt to ganger hvis du sender også. Fjern importen der først.',
                  { when: when(plan.file_exported.exported_at) },
                )}
              </span>
            </Notice>
          )}
          {plan.already_sent && (plan.already_sent.outcome ?? 'sent') === 'sent' && (
            <Notice tone="warn" title={T('Denne måneden er allerede sendt til PowerOffice.')}>
              {T(
                'Sendt {when}. Ikke last ned og importer PowerOffice-filen for samme måned i tillegg, da blir timene betalt to ganger.',
                { when: when(plan.already_sent.sent_at) },
              )}
              {st && (
                <span style={{ display: 'block' }} data-testid="poweroffice-sent-status">
                  {T(
                    'I PowerOffice: {locked} i lønnskjøring, {waiting} venter, {deleted} slettet, av {lines} linjer.',
                    { locked: st.locked, waiting: st.waiting, deleted: st.deleted, lines: st.lines },
                  )}
                </span>
              )}
              {st?.resendable && (
                <span style={{ display: 'block', marginTop: 6 }}>
                  <Button onClick={() => again()} disabled={busy}>
                    {T('Alle linjene er slettet i PowerOffice. Send på nytt')}
                  </Button>
                </span>
              )}
            </Notice>
          )}
          {plan.missing_people.some((p) => p.reason === 'shared_number') && (
            <Notice tone="dgr" title={T('Samme ansattnummer på flere ansatte')}>
              {plan.missing_people
                .filter((p) => p.reason === 'shared_number')
                .map((p) => `${p.name} (${T('ansattnr')} ${p.employee_number})`)
                .join(', ')}
              . {T('PowerOffice Go kan ikke skille dem. Gi hver sitt nummer på ansattkortet.')}
            </Notice>
          )}
          {plan.missing_people.some((p) => p.reason !== 'shared_number') && (
            <Notice tone="dgr" title={T('Finnes ikke i PowerOffice Go')}>
              {plan.missing_people
                .filter((p) => p.reason !== 'shared_number')
                .map(
                  (p) =>
                    `${p.name} (${p.employee_number ? `${T('ansattnr')} ${p.employee_number}` : T('mangler ansattnummer')})`,
                )
                .join(', ')}
              . {T('Ansattnummeret i Timefly må være det samme som i PowerOffice Go.')}
            </Notice>
          )}
          {plan.missing_types.length > 0 && (
            <Notice tone="dgr" title={T('Lønnsarter som ikke finnes i PowerOffice Go')}>
              {plan.missing_types
                .map((t) =>
                  t.reason === 'fixed_without_rate'
                    ? `${t.pay_type} (${t.code}: ${T('fast beløp i PowerOffice, men linjen har ingen sats')})`
                    : `${t.pay_type} (${t.code ?? T('mangler lønnsartnummer')})`,
                )
                .join(', ')}
              . {T('Rett nummeret under Innstillinger, Lønn.')}
            </Notice>
          )}
          <span>
            <Button tone="pri" onClick={send} disabled={busy || blocked}>
              {busy
                ? T('Sender…')
                : plan.file_exported && !plan.already_sent
                  ? T('Send likevel')
                  : T('Send til PowerOffice')}
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}
